/**
 * ThrottleService.ts
 * Provides unified throttling and debouncing functionality for API requests
 */

// Default throttle period in milliseconds
export const DEFAULT_THROTTLE_PERIOD = 500; // 0.5 second
// Default debounce period in milliseconds
export const DEFAULT_DEBOUNCE_PERIOD = 400; // 0.4 second

// Storage type enum for throttling
export enum ThrottleStorageType {
  MEMORY = "memory",
  LOCAL_STORAGE = "localStorage",
}

// In-memory throttle store
const memoryThrottleStore: Record<string, number> = {};

// In-memory debounce store for timeout IDs
const memoryDebounceStore: Record<string, NodeJS.Timeout> = {};

/**
 * Creates a throttle key based on URL, method, and function name
 * @param url - The URL of the request
 * @param method - The HTTP method (default: "GET")
 * @param functionName - The name of the request function (e.g. getAll, getBySlug, etc.)
 * @returns A unique key for the request
 */
export function createThrottleKey(
  url: string,
  method: string = "GET",
  functionName?: string
): string {
  // Extract the base endpoint without query params for more general throttling
  const baseUrl = url.split("?")[0];
  // Include function name in the throttle key if provided
  return functionName
    ? `throttle_${method}_${baseUrl}_${functionName}`
    : `throttle_${method}_${baseUrl}`;
}

/**
 * Checks if a request should be throttled
 * @param key - The throttle key for the request
 * @param period - The throttle period in milliseconds
 * @param storageType - The storage type to use (memory or localStorage)
 * @returns Boolean indicating whether the request should be throttled
 */
export function shouldThrottle(
  key: string,
  period: number = DEFAULT_THROTTLE_PERIOD,
  storageType: ThrottleStorageType = ThrottleStorageType.MEMORY
): boolean {
  const now = Date.now();
  let lastCallTime: number | null = null;

  // Get the last call time from the appropriate storage
  if (storageType === ThrottleStorageType.LOCAL_STORAGE) {
    const storedTime = localStorage.getItem(key);
    lastCallTime = storedTime ? parseInt(storedTime, 10) : null;
  } else {
    lastCallTime = memoryThrottleStore[key] || null;
  }

  // Check if the request should be throttled
  if (lastCallTime && now - lastCallTime < period) {
    console.log(
      `Request with key ${key} throttled - last request at ${new Date(
        lastCallTime
      ).toLocaleString()}`
    );
    return true;
  }

  // Update the last call time
  updateThrottleTimestamp(key, now, storageType);
  return false;
}

/**
 * Updates the timestamp for a throttled request
 * @param key - The throttle key for the request
 * @param timestamp - The timestamp to set (defaults to current time)
 * @param storageType - The storage type to use (memory or localStorage)
 */
export function updateThrottleTimestamp(
  key: string,
  timestamp: number = Date.now(),
  storageType: ThrottleStorageType = ThrottleStorageType.MEMORY
): void {
  if (storageType === ThrottleStorageType.LOCAL_STORAGE) {
    localStorage.setItem(key, timestamp.toString());
  } else {
    memoryThrottleStore[key] = timestamp;
  }
}

/**
 * Throttled fetch with headers
 * @param url - The URL to fetch
 * @param getAuthHeaders - Function to get authentication headers
 * @param options - Fetch options
 * @param throttlePeriod - The throttle period in milliseconds
 * @param storageType - The storage type to use for throttling
 * @param functionName - The name of the request function (e.g. getAll, getBySlug)
 * @returns Promise with the fetch result or { throttled: true } if throttled
 */
export async function throttledFetch(
  url: string,
  getAuthHeaders: () => Promise<Record<string, string>>,
  options: RequestInit = {},
  throttlePeriod: number = DEFAULT_THROTTLE_PERIOD,
  storageType: ThrottleStorageType = ThrottleStorageType.MEMORY,
  functionName?: string
) {
  const method = options.method || "GET";
  const throttleKey = createThrottleKey(url, method, functionName);

  // Check if request should be throttled
  if (shouldThrottle(throttleKey, throttlePeriod, storageType)) {
    return Promise.resolve({ throttled: true });
  }

  // Proceed with the fetch request
  const headers = await getAuthHeaders();
  return fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      ...headers,
    },
  }).then((res) => {
    if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
    return res.json();
  });
}

/**
 * Debounced fetch with headers - delays execution until after calls have stopped for a specified period
 * @param url - The URL to fetch
 * @param getAuthHeaders - Function to get authentication headers
 * @param options - Fetch options
 * @param debouncePeriod - The debounce period in milliseconds
 * @param functionName - The name of the request function (e.g. getBySubstring)
 * @returns Promise with the fetch result
 */
export function debouncedFetch(
  url: string,
  getAuthHeaders: () => Promise<Record<string, string>>,
  options: RequestInit = {},
  debouncePeriod: number = DEFAULT_DEBOUNCE_PERIOD,
  functionName?: string
): Promise<any> {
  const method = options.method || "GET";
  const debounceKey = createThrottleKey(url, method, functionName);

  // Clear existing timeout for this key
  if (memoryDebounceStore[debounceKey]) {
    clearTimeout(memoryDebounceStore[debounceKey]);
  }

  // Return a promise that resolves after the debounce period
  return new Promise((resolve, reject) => {
    memoryDebounceStore[debounceKey] = setTimeout(async () => {
      try {
        // Clean up the timeout reference
        delete memoryDebounceStore[debounceKey];

        // Proceed with the fetch request
        const headers = await getAuthHeaders();
        const response = await fetch(url, {
          ...options,
          headers: {
            ...(options.headers || {}),
            ...headers,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        resolve(data);
      } catch (error) {
        reject(error);
      }
    }, debouncePeriod);
  });
}
